import React, { useEffect, useState, useCallback } from "react";
import Table from "@components/table";
import { User } from "@apis/index";
import { Helper } from "@utils/index";
import styled from "@emotion/styled";
import { CheckCircle, XCircle, User as UserIcon } from "lucide-react";

const StatusBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid ${(props) => `${props.color}30`};
  text-transform: capitalize;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Referrals = ({ customerId }: { customerId: number }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await User.getReferrals({
        id: customerId,
        params: {
          limit: 10,
          page: page,
        },
      });
      setData(res.items || []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.totalItems || 0);
    } catch (error) {
      console.error("Failed to fetch referrals", error);
    } finally {
      setLoading(false);
    }
  }, [page, customerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: "S.N",
      key: "sn",
      width: "5%",
      render: (_: any, index: number) => (
        <span style={{ color: "#64748b", fontWeight: 600 }}>
          {(page - 1) * 10 + index + 1}
        </span>
      ),
    },
    {
      title: "Referred User",
      key: "user",
      width: "30%",
      render: (row: any) => (
        <UserInfo>
          <Avatar>
            {row.referredUser?.avatar ? (
              <img src={Helper.getFilePath(row.referredUser.avatar)} alt="" />
            ) : (
              <UserIcon size={16} />
            )}
          </Avatar>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>
              {row.referredUser?.name || "Unknown"}
            </span>
            <span style={{ fontSize: "12px", color: "#64748b" }}>
              ID: #{row.referTo}
            </span>
          </div>
        </UserInfo>
      ),
    },
    {
      title: "Phone & Email",
      key: "contact",
      width: "25%",
      render: (row: any) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontWeight: 600, color: "#334155" }}>
            {row.referredUser?.phoneNo || "---"}
          </span>
          <span style={{ fontSize: "12px", color: "#64748b" }}>
            {row.referredUser?.email || "---"}
          </span>
        </div>
      ),
    },
    {
      title: "Reward Status",
      key: "rewardStatus",
      width: "15%",
      render: (row: any) => (
        <StatusBadge color={row.isRewarded ? "#10b981" : "#f59e0b"}>
          {row.isRewarded ? <CheckCircle size={14} /> : <XCircle size={14} />}
          {row.isRewarded ? "Rewarded" : "Pending"}
        </StatusBadge>
      ),
    },
    {
      title: "Date",
      key: "createdAt",
      width: "20%",
      render: (row: any) => (
        <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "---"}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: "0" }}>
      <Table
        columns={columns}
        data={data}
        loading={loading}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
      />
    </div>
  );
};

export default Referrals;
